import React, {useContext, useRef} from 'react';
import { Toast } from "primereact/toast";
import { useFetch } from "../../../../../../layout/hooks/useFetch";
import PageHeading from "../../../../../../Components/PageHeading";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { usePagination } from "../../../../../../layout/hooks/usePagination";
import { InputSwitch } from "primereact/inputswitch";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../../../Router/routes";
import { webInfo } from "../../../../../../utils/webInfo";
import callToast, {postData} from "../../../../../../utils/helper";
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";


const ManageEmployee = () => {
    const toast = useRef<Toast | null>(null);
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [refresh, setRefresh] = React.useState<boolean>(false);
    const { accessToken } = useContext(LayoutContext);

    // pagination
    const { page, limitData, handlePageChange } = usePagination();

    // endpoint
    const mainTitle = "Manage Employees";
    const tableDataEndpointUrl = `/employees?page=${page}`;
    const statusSubmitEndpointUrl = `/active-status-change`;
    const adminApprovalSubmitEndpointUrl = `/admin-status-change`;

    // fetch data
    const {
        data: tableDataList,
        totalData,
        loading,
    } = useFetch(tableDataEndpointUrl, [refresh]);

    const nameTemplateBody = (rowData: any) => {
        return (
            <div
                className={"flex flex-column gap-1 cursor-pointer"}
                onClick={() => navigate(routes.manageEmployeeById.replace(":employeeId", rowData.id))}
            >
                <span className={"text-base font-semibold hover:text-blue-500 underline transition-colors"}>
                    {rowData.name}
                </span>
                <span className={"text-sm text-color-secondary"}>{rowData.email}</span>
            </div>
        );
    };

    const employmentInfoTemplateBody = (rowData: any) => {
        return (
            <div className={'flex flex-column gap-1'}>
                <span className={'text-base font-semibold'}>{rowData?.employee_designation?.name}</span>
                <span className={'text-sm text-color-secondary'}>{rowData?.employee_department?.name}</span>
            </div>
        );
    };

    const handleSubmit = async (rowId: number, type: "status" | "activeStatus", currentVal: number) => {
        let payload = {};
        let url = "";
        const newVal = currentVal === 1 ? 0 : 1;

        if (type === "activeStatus") {
            payload = { active_status: newVal };
            url = `${webInfo.baseUrl}/employees/${rowId}${statusSubmitEndpointUrl}`;
        } else if (type === "status") {
            payload = { admin_approval_status: newVal };
            url = `${webInfo.baseUrl}/employees/${rowId}${adminApprovalSubmitEndpointUrl}`;
        }

        try {
            setIsSubmitting(true);
            const response = await fetch(url, postData("PUT", accessToken, payload));

            if (response.ok) {
                callToast(toast, true, "Status updated successfully");
                setRefresh(!refresh);
                return;
            }
            const resData = await response.json();
            callToast(toast, false, resData.message || resData.error || "Update failed");

        } catch (err) {
            console.error('Failed to update', err);
            callToast(toast, false, "A network error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusActionBodyTemplate = (rowData: any) => {

        return (
            <InputSwitch
                checked={rowData.active_status === 1}
                disabled={isSubmitting}
                onChange={() => handleSubmit(rowData.id, "activeStatus", rowData.active_status)}
            />
        );
    };

    const adminApprovalActionBodyTemplate = (rowData: any) => {
        return (
            <InputSwitch
                checked={rowData.admin_approval_status === 1}
                disabled={isSubmitting}
                onChange={() => handleSubmit(rowData.id, "status", rowData.admin_approval_status)}
            />
        );
    };

    return (
        <div className={"card"}>
            <Toast ref={toast} />
            <PageHeading title={mainTitle} isCreate={true} createFunction={() => { navigate(routes.addEmployee) }} />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{ minWidth: '50rem' }}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column
                    field="name"
                    header="Name"
                    body={nameTemplateBody}
                    style={{ width: '30%' }}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    header={"Employment Information"}
                    body={employmentInfoTemplateBody}
                    style={{ width: '20%' }}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    header={"Role"}
                    style={{ width: '20%' }}
                    headerClassName={"white-space-nowrap"}
                    body={(rowData: any) => rowData?.roles?.map((role: any) => role.name).join(", ")}
                />
                <Column
                    header={"Status"}
                    style={{ width: '15%' }}
                    headerClassName={"white-space-nowrap"}
                    body={statusActionBodyTemplate}
                />
                <Column
                    header={"Admin Approval Status"}
                    style={{ width: '15%' }}
                    headerClassName={"white-space-nowrap"}
                    body={adminApprovalActionBodyTemplate}
                />
            </DataTable>
        </div>
    );
};

export default ManageEmployee;